/**
 * Normalised projection of a Descope user record to the minimal identity shape
 * the Kirin frontend cares about: `{ name, email }`.
 *
 * Pure, side-effect-free, and React/SDK-free — safe to call from React hooks,
 * React Query callbacks, plain utilities, or unit tests.
 *
 * Reused by:
 * - `useAlertMutes.buildOptimisticMute` to populate the actor on the
 *   optimistic mute patch (KIR-2747), avoiding the "Muted by Unknown user"
 *   flash that surfaced while the server response was in flight.
 * - (planned follow-up) `useAuth.normalizedUser`, which today inlines a
 *   near-identical projection without trimming.
 *
 * Contract:
 * - Returns `null` when `raw` is `null`, `undefined`, or not a plain object.
 * - Reads `name` and `email` from `raw`. Either field that is not a
 *   `string` (number / boolean / object / null / undefined / etc.) is treated
 *   as missing.
 * - Trims `name` and `email` once and **assigns the trimmed value back** —
 *   the value stored in the result matches the value used for the
 *   non-empty guards. (Prior reviewer caught a `.trim()`-as-guard-only
 *   regression; do NOT regress.)
 * - When both `name` and `email` are missing or whitespace-only, returns
 *   `null` (no usable identity).
 * - Otherwise `email` is the trimmed string (possibly `""` when no usable
 *   email was supplied but a name was), and `name` is the first of:
 *     1. trimmed `name` if non-empty
 *     2. `email.split('@')[0]` if non-empty
 *     3. the literal `"User"` — **reachable** when `email` is non-empty
 *        but its local part is empty (e.g. `"@example.com"`). Do NOT
 *        describe this branch as unreachable; the prior plan iteration
 *        flagged that exact docstring error.
 */
export function normalizeDescopeUser(
  raw: unknown
): { name: string; email: string } | null {
  if (raw === null || raw === undefined) return null;
  if (typeof raw !== 'object') return null;

  const record = raw as Record<string, unknown>;
  const rawName = record.name;
  const rawEmail = record.email;

  const trimmedName = typeof rawName === 'string' ? rawName.trim() : '';
  const trimmedEmail = typeof rawEmail === 'string' ? rawEmail.trim() : '';

  if (trimmedName === '' && trimmedEmail === '') return null;

  const email = trimmedEmail;
  let name: string;
  if (trimmedName !== '') {
    name = trimmedName;
  } else {
    const localPart = trimmedEmail.split('@')[0];
    name = localPart !== '' ? localPart : 'User';
  }

  return { name, email };
}
